import { isSubscriptionExpiredError } from "../utils/subscriptionExpiredDialog";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import TenantLink from "../components/TenantLink";
import PageTitle from "../components/PageTitle";
import { useAxiosPost } from "../hooks/useAxios";
import { ServiceType } from "../utils/enums";
import AddClientModal from "../components/AddClientModal";
import WarningImage from "../assets/images/Warning.png";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import {
  Avatar,
  Breadcrumb,
  Button,
  Modal,
  Tag,
  Spin,
  Radio,
  Upload,
  Typography,
  Alert,
  Timeline,
  Popover,
  Tooltip,
  List,
  Input,
  Progress,
} from "antd";
import { FaDownload, FaFileImport, FaList, FaPlus } from "react-icons/fa";
import { FaArrowsRotate } from "react-icons/fa6";
import moment from "moment";
import {
  CalendarOutlined,
  SyncOutlined,
  LoadingOutlined,
  InboxOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloseCircleFilled,
  DeleteFilled,
  ReloadOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  DownloadOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import FetchNoticesImg from "../assets/images/FETCHNOTICES.jpg";
import UniversalLoader from "../components/UniversalLoader";
import useScrollToTop from "../hooks/UseScrollToTop";
import {
  getClientDocType,
  getClientsFields,
  getRandomColor,
} from "../utils/helpers";
import {
  deleteClients,
  fetchData,
  getData,
  getNoticeDetails,
  getNoticeListData,
  importClientWrapper,
  importClientWrapperGet,
  postData,
} from "../api/apiService";
import axios from "axios";
import ImportPreviewTable from "./ImportPreviewTable";
import ImportLogsTable from "./ImportPreviewTable";
import DashboardButton from "../components/DashboardButton";
import {
  ArrowRight,
  FilterIcon,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Download,
} from "lucide-react";
import TableV2 from "../components/TableV2";
import { Table } from "antd";
import ImportClientsModal from "../components/ImportClientsModalV2";
import ErrorLogsTable from "../components/ErrorLogsTable";
import FetchNoticesConfirmModal from "../components/FetchNoticesConfirmModal";
import { CLIENTS, LAMBDA, EVENTS, DATA_IMPORT, DOCUMENTS } from "../api/endpointMap";
import UserFilterDropdown, {
  ALL_USERS,
  toQueryParam as userFilterToQueryParam,
} from "../components/UserFilterDropdown";
import ReassignClientsModal from "../components/ReassignClientsModal";
import DownloadDataButton from "../components/DownloadDataButton";
import { useEmbedToken } from "../context/EmbedTokenContext";
import { useIsAdmin, useEffectiveUserId } from "../hooks/useEffectiveRole";
import { useOrgUsers } from "../context/OrgUsersContext";

// USER_LEVEL_CLIENT_ASSIGNMENT.md: client list endpoints / assign URLs use
// `income-tax` / `gst` / `tds` URL segments — but ServiceType.GSTIN is the
// string `"gstin"` (legacy). Map between the two when calling assignment
// endpoints.
const SERVICE_TO_PORTAL_SEGMENT = {
  "income-tax": "income-tax",
  gstin: "gst",
  tds: "tds",
};

const { Text, Title } = Typography;

const urlMapping = {
  fetchClients: {
    [ServiceType.GSTIN]: CLIENTS.GST_LIST,
    [ServiceType.INCOME_TAX]: CLIENTS.INCOME_TAX_LIST,
    [ServiceType.TDS]: CLIENTS.TDS_LIST,
  },
  addClients: {
    [ServiceType.GSTIN]: CLIENTS.CREATE_GST,
    [ServiceType.INCOME_TAX]: CLIENTS.CREATE_INCOME_TAX,
    [ServiceType.TDS]: CLIENTS.CREATE_TDS,
  },
  syncNotices: {
    // GST: switched from the older /webhooks/lambda/process-selected-clients
    // route to the new dedicated /gst/fetch-notices/batch endpoint so the
    // GST flow mirrors the IT flow exactly (same payload shape, same
    // cooldown gate, same per-batch billing on completion).
    [ServiceType.GSTIN]: LAMBDA.TRIGGER_PARALLEL_FETCH_GST,
    // IT: repointed to the real batch endpoint. The previous URL was an
    // orphan (/webhooks/lambda/process-selected-clients) that 404'd. Payload
    // `{client_names: [...], portal_type: ...}` is already compatible.
    [ServiceType.INCOME_TAX]: LAMBDA.TRIGGER_PARALLEL_FETCH,
    [ServiceType.TDS]: EVENTS.TDS_PROCESS_SELECTED,
  },
  importClients: {
    [ServiceType.GSTIN]: CLIENTS.IMPORT,
    [ServiceType.INCOME_TAX]: CLIENTS.IMPORT,
    [ServiceType.TDS]: CLIENTS.IMPORT,
  },
  startImport: {
    [ServiceType.GSTIN]: DATA_IMPORT.START,
    [ServiceType.INCOME_TAX]: DATA_IMPORT.START,
    [ServiceType.TDS]: DATA_IMPORT.START,
  },
  docValidation: {
    [ServiceType.GSTIN]: DATA_IMPORT.GET_DOC,
    [ServiceType.INCOME_TAX]: DATA_IMPORT.GET_DOC,
    [ServiceType.TDS]: DATA_IMPORT.GET_DOC,
  },
  downloadTemplate: {
    [ServiceType.GSTIN]: CLIENTS.DOWNLOAD_IMPORT_TEMPLATE,
    [ServiceType.INCOME_TAX]: CLIENTS.DOWNLOAD_IMPORT_TEMPLATE,
    [ServiceType.TDS]: CLIENTS.DOWNLOAD_IMPORT_TEMPLATE,
  },

  checkImportStatus: {
    [ServiceType.GSTIN]: DATA_IMPORT.STATUS,
    [ServiceType.INCOME_TAX]: DATA_IMPORT.STATUS,
    [ServiceType.TDS]: DATA_IMPORT.STATUS,
  },

  checkImportLogs: {
    [ServiceType.GSTIN]: DATA_IMPORT.ERRORS_AND_WARNINGS,
    [ServiceType.INCOME_TAX]: DATA_IMPORT.ERRORS_AND_WARNINGS,
    [ServiceType.TDS]: DATA_IMPORT.ERRORS_AND_WARNINGS,
  },

  previewImport: {
    [ServiceType.GSTIN]: DATA_IMPORT.PREVIEW,
    [ServiceType.INCOME_TAX]: DATA_IMPORT.PREVIEW,
    [ServiceType.TDS]: DATA_IMPORT.PREVIEW,
  },
};

export const Clients = ({ serviceType }) => {
  const navigate = useNavigate();
  const [clientsData, setClientsData] = useState([]);
  const [fetchClientsData] = useAxiosPost(urlMapping.fetchClients[serviceType]);
  const [syncClientNoticesData] = useAxiosPost(
    urlMapping.syncNotices[serviceType]
  );
  const [addNewClient] = useAxiosPost(urlMapping.addClients[serviceType]);
  const [openClientModal, setOpenClientModal] = useState(false);
  const [clientLoading, setClientLoading] = useState(true);
  const [syncNoticeLoading, setSyncNoticesLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [fetchNoticeModal, setFetchNoticeModal] = useState(false);
  const [loadingClients, setLoadingClients] = useState({});
  const [pollingClients, setPollingClients] = useState({});
  const [isExpanded, setIsExpanded] = useState(false);

  // Filter persistence — sessionStorage-backed so column filters survive a
  // row-click → back-button round trip. Mirrors GstNoticesList.jsx pattern
  // (the working Notices reference) so behaviour stays consistent across
  // surfaces. Key scoped by serviceType + active/inactive so IT / GST / TDS
  // active and inactive each get their own slot — switching surfaces never
  // bleeds one filter set onto another.
  const navigationType = useNavigationType();
  const FILTER_STORAGE_KEY = `clients_filters_${serviceType}_${
    new URLSearchParams(window.location.search).get("type") || "active"
  }`;
  const [filterValues, setFilterValues] = useState(() => {
    const defaults = {
      client_name: "",
      username: "",
      last_sync_date: null,
      status: "",
    };
    // POP = browser back/forward → restore saved filters
    if (navigationType === "POP") {
      try {
        const saved = sessionStorage.getItem(FILTER_STORAGE_KEY);
        if (saved) return { ...defaults, ...JSON.parse(saved) };
      } catch (e) {
        /* ignore corrupt storage */
      }
      return defaults;
    }
    // Fresh navigation (sidebar click / dashboard card) → start clean
    try {
      sessionStorage.removeItem(FILTER_STORAGE_KEY);
    } catch (e) {
      /* ignore storage errors */
    }
    return defaults;
  });

  // Persist on every change. Cheap (JSON of 4 small fields); ignore
  // quota errors so a full sessionStorage can't break the page.
  useEffect(() => {
    try {
      sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filterValues));
    } catch (e) {
      /* ignore storage errors */
    }
  }, [filterValues, FILTER_STORAGE_KEY]);
  // User-filter (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.2). `ALL_USERS` is the
  // sentinel meaning "don't filter" — we omit assigned_user_id from the
  // request payload in that case so the BE applies role-default scope.
  const [userFilter, setUserFilter] = useState(ALL_USERS);
  // Reassign modal state — shared between row-action and bulk-action paths.
  // `mode` distinguishes: null = closed, "single" = one row, "bulk" =
  // multi-select. `targetClientIds` always carries the array of ids the
  // modal should operate on (single = [id], bulk = full selection).
  const [reassignMode, setReassignMode] = useState(null);
  const [reassignTargetIds, setReassignTargetIds] = useState([]);
  const [reassignLabel, setReassignLabel] = useState(null);
  // Single-row reassign carries the existing assignees (many-to-many
  // `assigned_users` array) so the popup can pre-select them. Bulk mode
  // leaves this empty (clients in the selection may have different
  // assignees → no sensible default).
  const [reassignCurrentUsers, setReassignCurrentUsers] = useState([]);
  const { isSingleUserOrg, users: orgUsers } = useOrgUsers();
  // Convert serviceType ("gstin" etc.) to the BE URL portal segment ("gst").
  const portalSegment = SERVICE_TO_PORTAL_SEGMENT[serviceType] || serviceType;
  // Reassign visibility (§7.3):
  //   - admin sees Reassign on every row + the bulk button
  //   - regular user sees Reassign only on rows they currently own
  //   - single-user-org → hidden everywhere (no other user to reassign to)
  //   - role="user" (normal user) → column hidden entirely (own clients
  //     are auto-assigned to Shared by AddClientModal anyway, so no
  //     reason to expose the action surface). Admin/sub_admin still see it.
  // Role resolution via the shared hook — correctly prefers the embed JWT
  // claims inside the /embed/* iframe so a stale localStorage admin session
  // in the same browser can't leak admin UI to a non-admin embed user.
  const { claims: embedClaims } = useEmbedToken();
  const isAdmin = useIsAdmin();
  const effectiveUserId = useEffectiveUserId();
  const showReassignFor = (record) => {
    if (isSingleUserOrg) return false;
    if (isAdmin) return true;
    // Regular user: only clients they're an assignee of are reassignable.
    // Many-to-many → membership check over the `assigned_users` array.
    // The BE enforces the same rule and would 403 anyway.
    return record?.assigned_users?.some((u) => u.user_id === effectiveUserId);
  };

  const customStyles = `
  .error-logs-table .ant-table-tbody > tr:hover {
    background-color: #fff7e6;
  }
  
  .error-logs-table .ant-table-cell {
    vertical-align: top;
  }
  
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

  const errorColumns = [
    {
      title: "Row(s)",
      dataIndex: "row_indexes",
      key: "row_indexes",
      width: 100,
      render: (rowIndexes) => {
        const rows = parseRowIndexes(rowIndexes);
        return <Tag color="blue">{rows}</Tag>;
      },
    },
    {
      title: "Client Name",
      dataIndex: "custom_client_name",
      key: "custom_client_name",
      width: 180,
      render: (name) => (
        <span className="font-medium text-gray-800">{name || "N/A"}</span>
      ),
    },
    {
      title: "Error Message",
      dataIndex: "messages",
      key: "messages",
      width: 250,
      render: (messages) => {
        const errorMsg = parseMessages(messages);
        return (
          <div className="flex items-start gap-2">
            <ExclamationCircleOutlined className="text-red-500 mt-1 flex-shrink-0" />
            <span className="text-red-600">{errorMsg}</span>
          </div>
        );
      },
    },
    // {
    //   title: "Technical Details",
    //   dataIndex: "exception",
    //   key: "exception",
    //   render: (exception) => {
    //     const mainError = extractMainError(exception);
    //     return (
    //       <Tooltip
    //         title={
    //           <pre className="text-xs max-h-60 overflow-auto">{exception}</pre>
    //         }
    //       >
    //         <div className="flex items-start gap-2 cursor-help">
    //           <WarningOutlined className="text-orange-500 mt-1 flex-shrink-0" />
    //           <span className="text-gray-600 text-sm line-clamp-2">
    //             {mainError}
    //           </span>
    //         </div>
    //       </Tooltip>
    //     );
    //   },
    // },
  ];

  // Import Client States
  const [importClientModal, setImportClientModal] = useState(false);
  const [importModuleType, setImportModuleType] = useState(serviceType);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dataImportId, setDataImportId] = useState(null);
  const [importStatus, setImportStatus] = useState("waiting");
  const [errorMsg, setErrorMsg] = useState(null);
  const [importLogs, setImportLogs] = useState([]);
  const [initiateLDeleteModal, setInitiateDeleteModal] = useState(false);
  const [totalRecordsInSheet, setTotalRecordsInSheet] = useState(0);
  const [importPollingInterval, setImportPollingInterval] = useState(null);
  const [realTimeImportLogs, setRealTimeImportLogs] = useState([]);

  const [syncCompletionModal, setSyncCompletionModal] = useState(false);
  const [completedClients, setCompletedClients] = useState([]);

  const [isProcessingAll, setIsProcessingAll] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [processedClients, setProcessedClients] = useState([]);

  // Batch-level polling state (for "Fetch All" runs)
  const [parallelFetchLogName, setParallelFetchLogName] = useState(null);
  const [fetchProgress, setFetchProgress] = useState(null);
  const [batchPollingInterval, setBatchPollingInterval] = useState(null);
  const isBatchFetchActive = useRef(false);
  // Realtime progress stream (SSE). Held in a ref so unmount / completion can
  // close it. When the stream is live we skip the 10s poll entirely; if it
  // errors we fall back to the poll below.
  const batchEventSource = useRef(null);

  // Two-step flow for the unified "Fetch Notices" button:
  //   1. fetchScopedBasicOpen → plain Yes/No confirm with selection count
  //   2. fetchScopedConfirmOpen → rich FetchNoticesConfirmModal with the
  //      per-row cooldown table (where the user actually picks which IDs
  //      to force-sync). One button covers both selected-clients and
  //      all-clients flows; selection presence drives the wording.
  const [fetchScopedBasicOpen, setFetchScopedBasicOpen] = useState(false);
  const [fetchScopedConfirmOpen, setFetchScopedConfirmOpen] = useState(false);

  // Single source of truth: is any notice-fetch flow currently running?
  // Used to disable every "Fetch Notices" / "Sync Notices" button across the
  // page so a user can't double-click and enqueue duplicate Lambda runs.
  const isAnyFetchInProgress =
    isProcessingAll ||
    syncNoticeLoading ||
    (!!fetchProgress && !fetchProgress.isComplete) ||
    Object.keys(loadingClients || {}).length > 0;

  //Pagination Items
  const [limitStart, setLimitStart] = useState(0);
  // Default 20 records per page. Page-length selector + "View All" in the
  // table footer change this (View All sets it to the total record count).
  const [limit, setLimit] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);

  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [logsData, setLogsData] = useState(null);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const searchClientType = queryParams.get("type") ?? "active";

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const [checkingRunningImport, setCheckingRunningImport] = useState(false);
  const [runningImportModal, setRunningImportModal] = useState(false);
  const [runningImportData, setRunningImportData] = useState(null);

  const [hasErrorLogs, setHasErrorLogs] = useState(false);
  const [checkingErrors, setCheckingErrors] = useState(false);
  const [errorLogsData, setErrorLogsData] = useState([]);
  const [errorImportName, setErrorImportName] = useState("");
  const [loadingErrorLogs, setLoadingErrorLogs] = useState(false);
  const [errorLogsModal, setErrorLogsModal] = useState(false);
  const [summaryData, setSummaryData] = useState(null);

  // const [fetchClientsCount] = useAxiosPost(DOCUMENTS.GET_COUNT);
  const { Dragger } = Upload;

  // Per-row Reassign action (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.3).
  // Returns null when the column shouldn't render at all:
  //   - single-user org (no other user to reassign to), OR
  //   - role="user" (normal user) — they create Shared-pool clients
  //     and shouldn't see the column at all.
  // For tenant_admin / sub_admin / super_admin / partner_admin the
  // column renders; cell self-suppresses per row via showReassignFor.
  // Build a fast lookup map of org users so the cell can render the
  // current assignee's `full_name (email)` without an N+1 search per
  // row. `orgUsers` is the assignable-roles-filtered list from
  // OrgUsersContext (user + tenant_admin). Assignees outside that
  // set (legacy data, sub_admin, etc.) fall through to a blank
  // display rather than showing a raw UUID.
  const usersById = useMemo(() => {
    const m = new Map();
    for (const u of orgUsers || []) m.set(u.id, u);
    return m;
  }, [orgUsers]);

  // Resolve an assignee's display name + initials from the BE payload,
  // falling back to the org-users map / email.
  const resolveAssigneeLabel = (au) => {
    const fromMap = usersById.get(au.user_id);
    return (
      au.user_name ||
      (fromMap && (fromMap.full_name || fromMap.email)) ||
      au.user_email ||
      au.user_id
    );
  };

  const initialsOf = (label) => {
    const parts = String(label || "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Render a client's current assignees (many-to-many) as named avatar
  // chips: a coloured initials circle + the user's name. Empty
  // `assigned_users` → a muted "Shared" pill. Designed for the dedicated
  // Users column.
  const formatAssignee = (assignedUsers) => {
    const list = Array.isArray(assignedUsers) ? assignedUsers : [];
    if (list.length === 0) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 text-xs font-medium">
          Shared
        </span>
      );
    }
    return (
      <div className="flex flex-col gap-1 items-start">
        {list.map((au) => {
          const label = resolveAssigneeLabel(au);
          return (
            <span
              key={au.user_id}
              className="inline-flex items-center gap-2 max-w-[200px]"
              title={au.user_email || label}
            >
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-[11px] font-semibold shrink-0">
                {initialsOf(label)}
              </span>
              <span className="text-xs text-slate-700 truncate">{label}</span>
            </span>
          );
        })}
      </div>
    );
  };

  // Dedicated "Users" column — shows who a client is currently assigned to
  // (many-to-many). Rendered separately from the Reassign action column,
  // and placed AFTER "Last Synced" and BEFORE "Reassign". Admin-only:
  // hidden for single-user orgs and for ALL non-admins (regular `user`
  // AND `read_only`), who can't manage assignments anyway.
  const usersColumn = isSingleUserOrg || !isAdmin
    ? null
    : {
        label: "Users",
        key: "_users",
        filterable: false,
        filterType: "text",
        preventRowClick: true,
        render: (row) => (
          <div className="flex justify-center">
            {formatAssignee(row?.assigned_users)}
          </div>
        ),
      };

  // Reassign action column — admin-only. Hidden for single-user orgs and
  // for ALL non-admins (regular `user` AND `read_only`).
  const reassignColumn = isSingleUserOrg || !isAdmin
    ? null
    : {
        label: "Reassign",
        key: "_reassign",
        filterable: false,
        filterType: "text",
        preventRowClick: true,
        render: (row) => {
          if (!showReassignFor(row)) return null;
          const id = row?.id || row?.name;
          if (!id) return null;
          return (
            <div className="flex justify-center mr-2">
              <Button
                size="small"
                onClick={() => {
                  setReassignTargetIds([id]);
                  setReassignLabel(row?.client_name || null);
                  setReassignCurrentUsers(row?.assigned_users || []);
                  setReassignMode("single");
                }}
              >
                Reassign
              </Button>
            </div>
          );
        },
      };

  const createLastSyncColumn = (syncField) => ({
    label: "Last Synced",
    key: syncField,
    filterable: false,
    filterType: "text",
    preventRowClick: true,
    render: (row) => {
      const clientId = row.name || row.id;

      // Truthy check — FastAPI returns `true`, legacy Frappe returned `1`.
      if (loadingClients[clientId] || row.is_processing) {
        return (
          <div className="flex items-center h-14 justify-center ">
            <Tag
              icon={<SyncOutlined spin />}
              color="#1890ff"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "160px",
                padding: "5px 10px",
              }}
            >
              Syncing notices...
            </Tag>
          </div>
        );
      }

      const formattedDate = row[syncField]
        ? moment(row[syncField]).format("MMMM Do YYYY, h:mm a")
        : "-";

      return row[syncField] ? (
        <div className="flex items-center h-14 justify-center mr-2">
          <Tag
            icon={<CalendarOutlined />}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "200px",
              padding: "5px 10px",
            }}
          >
            {formattedDate}
          </Tag>
        </div>
      ) : (
        <p className="flex justify-center mr-2">-</p>
      );
    },
  });

  const GSTColumns = [
    {
      label: "Client Name",
      key: "client_name",
      filterable: true,
      filterType: "text",
      filterName: "client_name",
    },
    {
      label: "Username",
      key: "gst_username",
      filterable: true,
      filterType: "text",
      filterName: "username",
    },
    // {
    //   label: "Notices",
    //   key: "total_notices",
    //   filterable: false,
    //   filterType: "text",
    //   render: (row) => {
    //     const total = row?.total_notices ?? 0;
    //     const notice = row?.gst_notice_count ?? 0;
    //     const additional = row?.gst_additional_notice_count ?? 0;
    //     return (
    //       <div className="flex items-center h-14 justify-center">
    //         <div className="flex flex-col items-center gap-1">
    //           <Tag
    //             color={total > 0 ? "#108ee9" : "default"}
    //             style={{ padding: "2px 12px", fontWeight: 600, margin: 0 }}
    //           >
    //             Total: {total}
    //           </Tag>
    //           <div className="text-xs" style={{ color: "#666" }}>
    //             Notice: <span style={{ fontWeight: notice > 0 ? 600 : 400, color: notice > 0 ? "#151c40" : "#999" }}>{notice}</span>
    //             {" · "}
    //             Additional: <span style={{ fontWeight: additional > 0 ? 600 : 400, color: additional > 0 ? "#151c40" : "#999" }}>{additional}</span>
    //           </div>
    //         </div>
    //       </div>
    //     );
    //   },
    // },
    {
      label: "Status",
      key: "disabled",
      filterable: false,
      filterType: "text",
      render: (row) => {
        return (
          <div className="flex items-center h-14 justify-center">
            {row?.disabled == "0" ? (
              <Tag color="#108ee9" style={{ padding: "2px 10px" }}>
                Active
              </Tag>
            ) : (
              <Tag color="#f50">Inactive</Tag>
            )}
          </div>
        );
      },
    },
    {
      label: "Sync Notices",
      key: "action",
      filterable: false,
      filterType: "text",
      preventRowClick: true,

      render: (row) => {
        return (
          <div className="flex items-center justify-center mr-4">
            <Button
              // Disabled when row is soft-deleted OR any fetch is running
              // anywhere on the page — prevents double-click / cross-click.
              disabled={row?.disabled == 1 || isAnyFetchInProgress}
              onClick={() => {
                setSelectedClient(row);
              }}
            >
              <FaArrowsRotate />
            </Button>
          </div>
        );
      },
    },
    createLastSyncColumn("last_gst_sync"),
    ...(usersColumn ? [usersColumn] : []),
    ...(reassignColumn ? [reassignColumn] : []),
  ];

  const TDSCOLUMNS = [
    {
      label: "Client Name",
      key: "client_name",
      filterable: true,
      filterType: "text",
      filterName: "client_name",
    },
    {
      label: "Username",
      key: "tds_username",
      filterable: true,
      filterType: "text",
      filterName: "username",
    },
    // {
    //   label: "Notices",
    //   key: "total_notices",
    //   filterable: false,
    //   filterType: "text",
    //   render: (row) => {
    //     const total = row?.total_notices ?? 0;
    //     return (
    //       <div className="flex items-center h-14 justify-center">
    //         <div className="flex flex-col items-center gap-1">
    //           <Tag
    //             color={total > 0 ? "#108ee9" : "default"}
    //             style={{ padding: "2px 12px", fontWeight: 600, margin: 0 }}
    //           >
    //             Total: {total}
    //           </Tag>
    //           <div className="text-xs" style={{ color: "#666" }}>
    //             Financial Notices
    //           </div>
    //         </div>
    //       </div>
    //     );
    //   },
    // },
    {
      label: "Status",
      key: "disabled",
      filterable: false,
      filterType: "text",

      render: (row) => {
        return (
          <div className="flex items-center h-14 justify-center">
            {row?.disabled == "0" ? (
              <Tag color="#108ee9" style={{ padding: "2px 10px" }}>
                Active
              </Tag>
            ) : (
              <Tag color="#f50">Inactive</Tag>
            )}
          </div>
        );
      },
    },
    {
      label: "Sync Notices",
      key: "action",
      filterable: false,
      filterType: "text",
      preventRowClick: true,

      render: (row) => {
        return (
          <div className="flex items-center justify-center mr-4">
            <Button
              disabled={row?.disabled == 1 || isAnyFetchInProgress}
              onClick={() => {
                setSelectedClient(row);
              }}
            >
              <FaArrowsRotate />
            </Button>
          </div>
        );
      },
    },
    createLastSyncColumn("last_tds_sync"),
    ...(usersColumn ? [usersColumn] : []),
    ...(reassignColumn ? [reassignColumn] : []),
  ];

  const INCOMETAXCOLUMNS = [
    {
      label: "Name Of Assessee",
      key: "client_name",
      filterable: true,
      filterType: "text",
      filterName: "client_name",
    },
    {
      label: "PAN",
      key: "username",
      filterable: true,
      filterType: "text",
      filterName: "username",
    },
    // {
    //   label: "Notices",
    //   key: "total_notices",
    //   filterable: false,
    //   filterType: "text",
    //   render: (row) => {
    //     const total = row?.total_notices ?? 0;
    //     const eproc = row?.eproceeding_count ?? 0;
    //     const demand = row?.demand_count ?? 0;
    //     return (
    //       <div className="flex items-center h-14 justify-center">
    //         <div className="flex flex-col items-center gap-1">
    //           <Tag
    //             color={total > 0 ? "#108ee9" : "default"}
    //             style={{ padding: "2px 12px", fontWeight: 600, margin: 0 }}
    //           >
    //             Total: {total}
    //           </Tag>
    //           <div className="text-xs" style={{ color: "#666" }}>
    //             E-Proc: <span style={{ fontWeight: eproc > 0 ? 600 : 400, color: eproc > 0 ? "#151c40" : "#999" }}>{eproc}</span>
    //             {" · "}
    //             Demand: <span style={{ fontWeight: demand > 0 ? 600 : 400, color: demand > 0 ? "#151c40" : "#999" }}>{demand}</span>
    //           </div>
    //         </div>
    //       </div>
    //     );
    //   },
    // },
    {
      label: "Status",
      key: "disabled",
      filterable: false,
      filterType: "text",
      render: (row) => {
        return (
          <div className="flex items-center h-14 justify-center ">
            {row?.disabled == "0" ? (
              <Tag color="#108ee9" style={{ padding: "2px 10px" }}>
                Active
              </Tag>
            ) : (
              <Tag color="#f50">Inactive</Tag>
            )}
          </div>
        );
      },
    },
    {
      label: "Sync Notices",
      key: "action",
      filterable: false,
      filterType: "text",
      preventRowClick: true,

      render: (row) => {
        return (
          <div className="flex items-center justify-center mr-4">
            <Button
              disabled={row?.disabled == 1 || isAnyFetchInProgress}
              onClick={() => {
                setSelectedClient(row);
              }}
            >
              <FaArrowsRotate />
            </Button>
          </div>
        );
      },
    },
    createLastSyncColumn("last_income_tax_sync"),
    ...(usersColumn ? [usersColumn] : []),
    ...(reassignColumn ? [reassignColumn] : []),
  ];

  const extractMainError = (exception) => {
    if (!exception) return "Unknown error";

    const lines = exception.split("\n").filter((line) => line.trim());

    const duplicateMatch = exception.match(
      /Duplicate entry '([^']+)' for key '([^']+)'/
    );
    if (duplicateMatch) {
      return `Duplicate entry '${duplicateMatch[1]}' for key '${duplicateMatch[2]}'`;
    }

    const lastLine = lines[lines.length - 1];
    if (lastLine) {
      return lastLine.replace(/^[a-z_]+\.[a-z_]+\.[A-Za-z]+:\s*/, "");
    }

    return "Error occurred during import";
  };

  const parseMessages = (messagesStr) => {
    try {
      const messages = JSON.parse(messagesStr);
      return messages.map((msg) => msg.message).join(", ");
    } catch (e) {
      return messagesStr;
    }
  };

  const parseRowIndexes = (rowIndexesStr) => {
    try {
      const rows = JSON.parse(rowIndexesStr);
      return rows.join(", ");
    } catch (e) {
      return rowIndexesStr;
    }
  };

  const handleOk = () => {
    setFetchNoticeModal(false);
    // FastAPI row PK is `id`; legacy Frappe used `name` — accept either.
    syncClientNotices([selectedClient?.id || selectedClient?.name]);
  };

  const handleCancel = () => {
    setFetchNoticeModal(false);
    setSelectedClient(null);
  };

  const checkRunningImport = async () => {
    setCheckingRunningImport(true);
    try {
      const doctype = getClientDocType(serviceType);
      // Legacy `POST /api/v1/clients/import/status` with {doctype} returns 405
      // on FastAPI — that path is GET-only and expects `data_import_id`.
      // Use working `GET /api/v1/clients/import/last?doctype=<X>` which
      // returns the most recent import; if its status is Pending/Processing,
      // a run is in flight and we show the modal. Same fix as IncomeTax.jsx.
      const response = await getNoticeDetails(
        `/api/v1/clients/import/last?doctype=${encodeURIComponent(doctype)}`
      );
      const lastImport = response?.message?.data;
      const isRunning =
        lastImport && ["Pending", "Processing"].includes(lastImport.status);

      if (isRunning) {
        setRunningImportData({
          imports: [
            {
              status: lastImport.status,
              data_import: lastImport.file_name || lastImport.id,
              user: "-",
              started_at: lastImport.created_at,
            },
          ],
        });
        setRunningImportModal(true);
      } else {
        openImportClientModal();
      }
    } catch (err) {
      console.error("Error checking running import:", err);
      toast.error("Failed to check import status. Please try again.");
    } finally {
      setCheckingRunningImport(false);
    }
  };

  const pollClientStatus = async (clientName, intervalId) => {
    // FastAPI per-portal single-client GET. The old Frappe-shape
    //   GET /{DocType}?filters={...}&fields=[...]
    // route never existed in this backend and 404'd, breaking the polling loop
    // (and firing the "Sync Completed" popup at the wrong time).
    const listUrlByPortal = {
      "income-tax": CLIENTS.INCOME_TAX_LIST,
      "gstin": CLIENTS.GST_LIST,
      "tds": CLIENTS.TDS_LIST,
    };
    const baseUrl = listUrlByPortal[serviceType];
    if (!baseUrl) {
      clearInterval(intervalId);
      return;
    }

    try {
      // Response is a single flat dict (not {data: [...]}). See
      // backend `_it_client_to_dict` / `_gst_client_to_dict` / `_tds_client_to_dict`.
      const data = await fetchData(`${baseUrl}/${clientName}`);

      // Falsy check — FastAPI returns `false`, legacy Frappe returned `0`.
      if (data && !data.is_processing) {
        clearInterval(intervalId);

        // Update the client row in clientsData with fresh data (updates Last Sync date).
        // Match on either `id` (FastAPI) or `name` (legacy Frappe) so list rows
        // that came from mixed sources still reconcile correctly.
        const freshClient = data;
        setClientsData((prev) =>
          prev.map((c) =>
            (c.id || c.name) === clientName ? { ...c, ...freshClient } : c,
          ),
        );

        setPollingClients((prev) => {
          const updated = { ...prev };
          delete updated[clientName];
          return updated;
        });

        setCompletedClients((prev) => {
          if (!prev.some((client) => (client.id || client.name) === clientName)) {
            return [
              ...prev,
              {
                name: clientName,
                client_name: data.client_name,
              },
            ];
          }
          return prev;
        });

        setLoadingClients((prevLoadingClients) => {
          const updatedLoadingClients = { ...prevLoadingClients };
          delete updatedLoadingClients[clientName];

          const remainingClients = Object.keys(updatedLoadingClients);
          if (remainingClients.length === 0 && !isBatchFetchActive.current) {
            // Refresh full client list data before showing completion modal
            getClientsData(limitStart, limit);
            setTimeout(() => setSyncCompletionModal(true), 100);
          }

          return updatedLoadingClients;
        });
      }
    } catch (err) {
      console.error("Error polling client status:", err);

      clearInterval(intervalId);

      setPollingClients((prev) => {
        const updated = { ...prev };
        delete updated[clientName];
        return updated;
      });

      setLoadingClients((prevLoadingClients) => {
        const updatedLoadingClients = { ...prevLoadingClients };
        delete updatedLoadingClients[clientName];

        const remainingClients = Object.keys(updatedLoadingClients);
        if (remainingClients.length === 0) {
          setTimeout(() => setSyncCompletionModal(true), 100);
        }

        return updatedLoadingClients;
      });
    }
  };

  useEffect(() => {
    if (selectedClient) {
      setFetchNoticeModal(true);
    }
  }, [selectedClient]);

  const columns =
    serviceType === "gstin"
      ? GSTColumns
      : serviceType === "tds"
      ? TDSCOLUMNS
      : INCOMETAXCOLUMNS;

  // const getClientsCount = (docType) => {
  //   const filters = buildFilters();
  //   fetchClientsCount({
  //     payload: {
  //       doctype: docType,
  //       filters: filters,
  //     },
  //     cb: (data) => {
  //       setTotalRecords(data?.message);
  //     },
  //   });
  // };
  const getClientsCount = () => {}; // no-op — totalRecords is now set inside getClientsData via data.total

  // Fetch ALL clients (all pages) matching current filters — used by
  // TableV2 PDF/CSV download buttons. Backend caps page_size at 100 so
  // loop in batches until last page returns < 100. Mirrors getClientsData
  // params/endpoint exactly.
  const fetchAllForExport = async () => {
    const PAGE_SIZE = 100;
    const fetchUrl = urlMapping.fetchClients[serviceType];
    const isInactive = searchClientType !== "active";
    const usernameParam =
      serviceType === "gstin"
        ? "gst_username"
        : serviceType === "tds"
        ? "tds_username"
        : "username";

    const all = [];
    let page = 1;
    while (true) {
      const params = {
        page,
        page_size: PAGE_SIZE,
        only_disabled: isInactive,
      };
      if (filterValues.client_name) params.client_name = filterValues.client_name;
      if (filterValues.username) params[usernameParam] = filterValues.username;
      const userFilterParam = userFilterToQueryParam(userFilter);
      if (userFilterParam !== undefined) params.assigned_user_id = userFilterParam;

      const data = await fetchData(fetchUrl, params);
      const batch = data?.items || [];
      all.push(...batch);
      if (batch.length < PAGE_SIZE) break;
      page += 1;
    }
    return all;
  };

  const getClientsData = async (start, itemsPerPage) => {
    setClientLoading(true);
    const fetchUrl = urlMapping.fetchClients[serviceType];
    const page = Math.floor(start / itemsPerPage) + 1;
    const isInactive = searchClientType !== "active";

    // Map UI 'username' field to the correct backend param per service type
    const usernameParam =
      serviceType === "gstin"
        ? "gst_username"
        : serviceType === "tds"
        ? "tds_username"
        : "username";

    const params = {
      page: page,
      page_size: itemsPerPage,
      only_disabled: isInactive,
    };
    if (filterValues.client_name) {
      params.client_name = filterValues.client_name;
    }
    if (filterValues.username) {
      params[usernameParam] = filterValues.username;
    }
    // User-filter (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.2). Omit the param
    // entirely when "All users" is selected so BE applies the role-based
    // default visibility scope (admin = all org clients, regular user =
    // own + Shared). For "Unassigned" we send the literal string per spec
    // §5.1; for a specific user we send the uuid.
    const userFilterParam = userFilterToQueryParam(userFilter);
    if (userFilterParam !== undefined) {
      params.assigned_user_id = userFilterParam;
    }

    try {
      const data = await fetchData(fetchUrl, params);

      const items = data?.items || [];
      const total = data?.total || 0;

      setClientsData(items);
      setTotalRecords(total);
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to load clients");
    } finally {
      setTimeout(() => {
        setClientLoading(false);
      }, 1000);
    }
  };

  const syncClientNotices = (clientNames) => {
    setSyncNoticesLoading(true);

    setCompletedClients([]);

    const newLoadingClients = { ...loadingClients };
    const newPollingClients = { ...pollingClients };

    clientNames.forEach((clientName) => {
      newLoadingClients[clientName] = true;
    });
    setLoadingClients(newLoadingClients);

    const portalType = serviceType === "gstin" ? "gst" : serviceType === "tds" ? "tds" : "income_tax";
    syncClientNoticesData({
      payload: {
        client_names: clientNames,
        portal_type: portalType,
        // Explicit per-client sync = an intentional "fetch THIS client now"
        // action, so it must bypass the 48-hour redundancy cooldown — the same
        // bypass the top popup's single-client selection already sends. Without
        // it, every batch endpoint (GST/IT/TDS) silently skips a client synced
        // in the last 48h and returns 200 with an empty client list, so the
        // button reported "synced successfully" while fetching nothing. The
        // partner WEEKLY cooldown is a separate billing gate and is NOT
        // bypassed here (it still returns 429 with its own message).
        force_sync_client_ids: clientNames,
      },
      cb: (data, errorMessage) => {
        setSyncNoticesLoading(false);

        if (!data || errorMessage) {
          toast.error(errorMessage || "Failed to start sync process");
          setLoadingClients({});
          setPollingClients({});
          return;
        }

        toast.info("Notices are being fetched. This may take a while.");
        setSelectedClient(null);
        setSelectedClientIds([]);

        // Capture log_name and start progress bar polling (GST/IT Lambda path).
        // FastAPI nests the service result under data.data.result; legacy
        // Frappe callers used data.message / data.result. Check the new path
        // first, fall through to the old ones for backward compatibility.
        const result =
          data?.data?.result || data?.message || data?.result || {};
        const logName = result?.log_name;
        if (logName) {
          setParallelFetchLogName(logName);
          startBatchPolling(logName);
        }

        clientNames.forEach((clientName) => {
          const intervalId = setInterval(() => {
            pollClientStatus(clientName, intervalId);
          }, 5000);

          newPollingClients[clientName] = intervalId;
        });

        setPollingClients(newPollingClients);
      },
    });
  };

  useEffect(() => {
    if (selectedClient == null) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  }, [selectedClient]);

  useEffect(() => {
    if (importClientModal) {
      checkForPreviousErrors();
    }
  }, [importClientModal]);

  useEffect(() => {
    const docType = getClientDocType(serviceType);
    getClientsCount(docType);
  }, [filterValues, searchClientType]);

  const handleAddClient = async (payload) => {
    try {
      addNewClient({
        payload,
        cb: (data, msg) => {
          if (data == null) {
            toast.error(msg ?? "Error in creating client");
          } else {
            setOpenClientModal(false);
            toast.info("Added Client Successfully", {
              position: "top-right",
              autoClose: 5000,
            });
            const docType = getClientDocType(serviceType);
            getClientsCount(docType);
            getClientsData(limitStart, limit);
          }
        },
      });
    } catch (err) {
      toast.error(err);
    }
  };

  // Check for active fetch on page load (handles page refresh mid-fetch or navigation from another tab)
  // Scoped by portal type so IT/GST/TDS fetches are independent
  const checkActiveFetch = async () => {
    try {
      const portalTypeMap = {
        [ServiceType.GSTIN]: "GST",
        [ServiceType.INCOME_TAX]: "Income Tax",
        [ServiceType.TDS]: "TDS",
      };
      const response = await postData(
        LAMBDA.GET_FETCH_STATUS,
        { portal_type: portalTypeMap[serviceType] || "Income Tax" }
      );
      // FastAPI returns service payload under response.data.result (same as startBatchPolling).
      const result =
        response?.data?.result || response?.result || response?.message || {};
      if (result?.status === "success" && !result?.is_complete) {
        setIsProcessingAll(true);
        setParallelFetchLogName(result.log_name);
        startBatchPolling(result.log_name);
      }
    } catch (err) {
      console.error("Error checking active fetch:", err);
    }
  };

  // Reset to first page when toggling between active and inactive tabs
  useEffect(() => {
    if (limitStart !== 0) setLimitStart(0);
  }, [searchClientType]);

  useEffect(() => {
    getClientsData(limitStart, limit);
    checkActiveFetch();
    // userFilter is intentionally a dep — flipping the user-filter
    // dropdown should re-fetch the page-1 list with the new scope.
  }, [limitStart, limit, filterValues, searchClientType, userFilter]);

  // Auto-detect clients with is_processing=1 on page load/navigation and start polling
  // Skip per-client polling when batch-level polling is active (Fetch All scenario)
  useEffect(() => {
    if (!clientsData || clientsData.length === 0) return;
    if (isBatchFetchActive.current) return;

    const processingClients = clientsData.filter(
      (client) => client.is_processing,
    );

    if (processingClients.length === 0) return;

    const newLoadingClients = { ...loadingClients };
    const newPollingClients = { ...pollingClients };

    processingClients.forEach((client) => {
      // FastAPI returns `id` as the PK; legacy Frappe used `name`. Fall back
      // so the auto-detect loop keys rows correctly under either backend.
      const clientKey = client.id || client.name;
      if (!clientKey) return;

      // Skip if already polling this client
      if (newPollingClients[clientKey]) return;

      newLoadingClients[clientKey] = true;

      const intervalId = setInterval(() => {
        pollClientStatus(clientKey, intervalId);
      }, 5000);

      newPollingClients[clientKey] = intervalId;
    });

    setLoadingClients((prev) => ({ ...prev, ...newLoadingClients }));
    setPollingClients((prev) => ({ ...prev, ...newPollingClients }));
  }, [clientsData]);

  const handleRowRedirection = (row) => {
    switch (serviceType) {
      case ServiceType.GSTIN:
        return `${row?.id || row?.name}`;

      case ServiceType.TDS:
        return `${row?.id || row?.name}`;

      default:
        return "/404";
    }
  };

  const checkForPreviousErrors = async () => {
    setCheckingErrors(true);
    try {
      // Legacy `POST /api/v1/clients/import/last` body `{client_type}` returns
      // 405 on FastAPI — that path is GET-only with `?doctype=` query. Switch
      // to GET and derive the "previous import had errors" flag from the
      // returned row's status (`Error` or `Partial Success`) instead of the
      // old boolean `result.error_in_import` the Frappe endpoint returned.
      const doctype = getClientDocType(serviceType);
      const response = await getNoticeDetails(
        `/api/v1/clients/import/last?doctype=${encodeURIComponent(doctype)}`
      );
      const lastImport = response?.message?.data;
      const hadErrors =
        lastImport && ["Error", "Partial Success"].includes(lastImport.status);

      if (hadErrors) {
        setHasErrorLogs(true);
        setErrorImportName(lastImport.id);
      }
    } catch (err) {
      console.error("Error checking previous errors:", err);
    } finally {
      setCheckingErrors(false);
    }
  };

  const fetchErrorLogs = async () => {
    setLoadingErrorLogs(true);
    setErrorLogsModal(true);

    try {
      const response = await importClientWrapperGet(
        urlMapping.checkImportLogs[serviceType],
        {
          data_import_name: errorImportName,
        }
      );

      if (response && response.result) {
        const { import_errors, import_successes, template_warnings, summary } =
          response.result;

        const combinedLogs = [
          ...template_warnings.map((warning) => ({
            row_indexes: [warning.row],
            client_name: "-",
            status: "warning",
            messages: [{ message: warning.message }],
            fieldname: warning.field?.fieldname || "-",
          })),
          ...import_errors.map((error) => ({
            row_indexes: error.row_indexes,
            client_name: error.client_name,
            status: "error",
            messages: error.messages,
            log_name: error.log_name,
          })),
          ...import_successes.map((success) => ({
            row_indexes: success.row_indexes,
            client_name: success.client_name,
            status: "success",
            messages: JSON.parse(success.messages),
            log_name: success.log_name,
          })),
        ];

        setErrorLogsData(combinedLogs);
        setSummaryData(summary);
      }
    } catch (err) {
      toast.error("Failed to fetch error logs");
    } finally {
      setLoadingErrorLogs(false);
    }
  };

  const downloadErrorTemplate = async () => {
    try {
      const response = await postData(
        DATA_IMPORT.DOWNLOAD_ERRORED,
        {
          data_import_name: errorImportName,
        }
      );

      const csvContent = await response;

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `error_template_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Error downloading template:", err);
    }
  };

  // Import Client Functions
  const openImportClientModal = () => {
    setImportModuleType(serviceType);
    setFileList([]);
    setDataImportId(null);
    setImportStatus("waiting");
    setImportLogs([]);
    setImportClientModal(true);
  };

  const closeImportClientModal = () => {
    // Clear polling interval
    if (importPollingInterval) {
      clearInterval(importPollingInterval);
      setImportPollingInterval(null);
    }

    setImportClientModal(false);
    setFileList([]);
    setDataImportId(null);
    setImportStatus("waiting");
    setImportLogs([]);
    setRealTimeImportLogs([]);
    setErrorMsg(null);
    setTotalRecordsInSheet(0);
  };

  const getColumnWidths = () => {
    return {
      client_name: "25%",
      username: "20%",
      status: "15%",
      sync_action: "15%",
      last_sync: "25%",
    };
  };

  const columnWidths = getColumnWidths();

  useEffect(() => {
    return () => {
      if (importPollingInterval) {
        clearInterval(importPollingInterval);
      }
    };
  }, [importPollingInterval]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const importParam = searchParams.get("import");

    if (importParam === "true") {
      openImportClientModal();

      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete("import");
      const newSearch = newSearchParams.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;
      navigate(newUrl, { replace: true });
    }
  }, [location.search]);

  const handleModuleTypeChange = (e) => {
    setImportModuleType(e.target.value);
  };

  const downloadTemplate = async () => {
    try {
      const doctype =
        importModuleType === ServiceType.INCOME_TAX
          ? "Income Tax Client"
          : importModuleType === ServiceType.GSTIN
          ? "GST Client"
          : "TDS Client";

      // Legacy `POST /api/v1/clients/import/template` with body {doctype}
      // returns 405 on FastAPI — that path is GET-only with `?doctype=` query.
      // Switch to GET; body stays empty, doctype goes as a query param. Binary
      // XLSX response handling (responseType: "blob") is unchanged.
      const requestUrl = `${process.env.REACT_APP_API_BASE_URL}${urlMapping.downloadTemplate[serviceType]}?doctype=${encodeURIComponent(doctype)}`;
      const response = await axios.get(requestUrl, {
        responseType: "blob",
        transitional: {
          forcedJSONParsing: false,
        },
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${doctype.replace(" ", "_")}_template.xlsx`;
      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Template downloaded successfully");
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Error downloading template");
    }
  };

  const uploadProps = {
    onRemove: (file) => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      const isExcel =
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel";
      if (!isExcel) {
        toast.error("You can only upload Excel files!");
        return Upload.LIST_IGNORE;
      }
      setFileList([file]);
      return false;
    },
    fileList,
  };

  const handleFetchAllClientsNotices = async (forceSyncClientIds = []) => {
    setIsProcessingAll(true);

    try {
      let apiUrl = "";
      let payload = {};

      if (serviceType === "gstin") {
        // GST has its own batch endpoint; the IT one (TRIGGER_PARALLEL_FETCH)
        // returns 404 "No eligible Income Tax clients" for a GST page.
        // /gst/fetch-notices/batch auto-picks all active GST clients when
        // client_names is empty.
        apiUrl = LAMBDA.TRIGGER_PARALLEL_FETCH_GST;
        payload = {};
      } else if (serviceType === "tds") {
        apiUrl = EVENTS.TDS_PROCESS_ALL;
      } else {
        apiUrl = LAMBDA.TRIGGER_PARALLEL_FETCH;
        payload = { portal_type: "income_tax" };
      }

      // Pass the popup's selection so the BE bypasses the 48-hour
      // cooldown for these client IDs only.
      if (Array.isArray(forceSyncClientIds) && forceSyncClientIds.length > 0) {
        payload.force_sync_client_ids = forceSyncClientIds;
        // For batch endpoints, also constrain client_names to the
        // selected rows so we don't kick off unselected clients.
        payload.client_names = forceSyncClientIds;
      }

      const response = await postData(apiUrl, payload);

      // FastAPI nests the service result under response.data.result.
      // Legacy Frappe used response.result / response.message directly.
      const result =
        response?.data?.result || response?.result || response?.message || {};
      setProcessedClients(result?.client_names || []);

      // Capture master log_name for batch-level polling (single log tracks ALL batches)
      const masterLogName = result?.log_name;
      const batchResults = result?.batch_results || [];
      if (masterLogName) {
        setParallelFetchLogName(masterLogName);
      } else if (batchResults.length > 0 && batchResults[0]?.log_name) {
        setParallelFetchLogName(batchResults[0].log_name);
      }

      // Show success modal immediately — the BE has already returned the
      // "queued" confirmation by the time we get here, so the prior 2-sec
      // setTimeout was just artificial UX padding that made the popup feel
      // slow.
      setShowSuccessModal(true);
    } catch (error) {
      // An expired plan is a handled state: the interceptor already
      // showed the upgrade dialog — no console noise, no toast.
      if (isSubscriptionExpiredError(error)) {
        setIsProcessingAll(false);
        return;
      }
      console.error("Error processing all clients:", error);
      // Partner weekly notice-fetch cooldown — the BE returns 429 with a
      // structured payload (status: "Notice Fetch Cooldown" + a message).
      // Surface that message verbatim instead of the generic failure toast.
      //
      // Important: postData (apiService.js) calls parseErrorMessage and
      // re-throws the PARSED STRING for most 4xx responses — by the time
      // we land here, `error` is often a plain string like
      // "Your organisation already fetched notices this week ...", not
      // an axios error object. The earlier shape-only check
      // (`error?.response?.data...`) silently failed on the string branch
      // and fell through to the generic toast. Handle both shapes.
      const beStringMsg = typeof error === "string" ? error : null;
      const errData = error?.response?.data?.detail || error?.response?.data || {};
      const isCooldown =
        errData?.status === "Notice Fetch Cooldown" ||
        errData?.data?.status === "Notice Fetch Cooldown" ||
        /once per week|Notice Fetch Cooldown/i.test(beStringMsg || "");
      const cooldownMsg =
        beStringMsg ||
        errData?.message ||
        errData?.data?.message ||
        error?.response?.data?.message;
      if (isCooldown && cooldownMsg) {
        // The app-wide ToastContainer (src/index.js) is set to
        // theme="colored", which paints `toast.warning` as yellow
        // background with WHITE text — the cooldown message becomes
        // nearly invisible on screen. Override the text + icon colour
        // to dark grey just for this toast so the message reads cleanly
        // without changing the global theme (which would risk regressing
        // every other toast in the app).
        toast.warning(cooldownMsg, {
          style: { color: "#1f2937" },          // dark text
          progressStyle: { background: "#1f2937" },
        });
      } else if (cooldownMsg) {
        // Any other BE-supplied error message — show it instead of the
        // useless generic "Failed to queue all clients" toast.
        toast.error(cooldownMsg);
      } else {
        toast.error("Failed to queue all clients for processing");
      }
      setIsProcessingAll(false);
    }
  };

  // Start polling the Parallel Fetch Log for batch-level progress
  // Realtime notice-fetch progress.
  //
  // Preferred path: a Server-Sent-Events stream (`GET /gst/fetch-events/{log}`)
  // that PUSHES the succeeded/failed counts the instant each worker webhook
  // lands — no 10s lag. If the stream can't open or errors (proxy strips SSE,
  // Redis down, older browser) we transparently fall back to the original 10s
  // poll so the banner still advances. Both paths feed the SAME progress
  // handler and the SAME completion cleanup.
  const startBatchPolling = (logName) => {
    // Tear down any in-flight tracker (interval or stream) before starting.
    if (batchPollingInterval) {
      clearInterval(batchPollingInterval);
      setBatchPollingInterval(null);
    }
    if (batchEventSource.current) {
      try {
        batchEventSource.current.close();
      } catch (e) {
        /* ignore */
      }
      batchEventSource.current = null;
    }

    isBatchFetchActive.current = true;

    setFetchProgress({
      total: 0,
      successful: 0,
      failed: 0,
      status: "Processing",
      isComplete: false,
    });

    // Shared completion cleanup — runs once when the run reports complete.
    const finishFetch = () => {
      if (batchPollingInterval) {
        clearInterval(batchPollingInterval);
      }
      setBatchPollingInterval(null);
      if (batchEventSource.current) {
        try {
          batchEventSource.current.close();
        } catch (e) {
          /* ignore */
        }
        batchEventSource.current = null;
      }
      isBatchFetchActive.current = false;
      getClientsData(limitStart, limit); // refresh client data
      setSyncCompletionModal(true); // completion modal
    };

    // Shared snapshot handler — SSE and poll both hand it the same shape
    // ({ status, total_clients, successful_count, failed_count, fetch_status,
    //   is_complete, ... }). Returns true once the run is complete.
    const applyProgress = (result) => {
      if (result?.status !== "success") return false;
      const progress = {
        total: result.total_clients || 0,
        successful: result.successful_count || 0,
        failed: result.failed_count || 0,
        status: result.fetch_status || "Processing",
        isComplete: result.is_complete || false,
        portalType: result.portal_type || "",
        portalFailureCount: result.portal_failure_count || 0,
        credentialFailureCount: result.credential_failure_count || 0,
      };
      setFetchProgress(progress);
      if (progress.isComplete) {
        finishFetch();
        return true;
      }
      return false;
    };

    // --- Fallback: original 10s poll ---
    const startPoll = () => {
      if (batchPollingInterval) clearInterval(batchPollingInterval);
      const intervalId = setInterval(async () => {
        try {
          const response = await postData(LAMBDA.GET_FETCH_STATUS, {
            log_name: logName,
          });
          // FastAPI returns the payload under response.data.result; legacy
          // Frappe returned it at response.result / response.message.
          const result =
            response?.data?.result ||
            response?.result ||
            response?.message ||
            {};
          applyProgress(result);
        } catch (err) {
          console.error("Error polling batch status:", err);
        }
      }, 10000);
      setBatchPollingInterval(intervalId);
    };

    // --- Preferred: SSE stream ---
    const startSse = () => {
      // EventSource can't set an Authorization header, so the JWT rides as a
      // query param the BE validates on connect.
      if (typeof window === "undefined" || !("EventSource" in window)) {
        startPoll();
        return;
      }
      const token = localStorage.getItem("authToken") || "";
      const base = process.env.REACT_APP_API_BASE_URL || "";
      const url = `${base}${LAMBDA.FETCH_EVENTS}/${encodeURIComponent(
        logName
      )}?token=${encodeURIComponent(token)}`;

      let gotFirstMessage = false;
      let es;
      try {
        es = new EventSource(url);
      } catch (e) {
        startPoll();
        return;
      }
      batchEventSource.current = es;

      es.onmessage = (evt) => {
        gotFirstMessage = true;
        try {
          const result = JSON.parse(evt.data);
          applyProgress(result); // finishFetch() closes es on completion
        } catch (e) {
          /* keep-alive / non-JSON line — ignore */
        }
      };

      es.onerror = () => {
        // If the stream never delivered a single message, the transport is
        // likely unavailable (proxy, Redis, CORS) — fall back to the poll.
        // If it errored mid-stream (network blip) and the run is still active,
        // also fall back so progress never stalls. EventSource auto-reconnects
        // otherwise, but we prefer the deterministic poll here.
        try {
          es.close();
        } catch (e) {
          /* ignore */
        }
        if (batchEventSource.current === es) batchEventSource.current = null;
        if (isBatchFetchActive.current) {
          if (!gotFirstMessage) {
            console.warn("SSE unavailable - falling back to poll");
          } else {
            console.warn("SSE dropped mid-run - falling back to poll");
          }
          startPoll();
        }
      };
    };

    startSse();
  };

  // Cleanup batch polling + stream on unmount
  useEffect(() => {
    return () => {
      if (batchPollingInterval) {
        clearInterval(batchPollingInterval);
      }
      if (batchEventSource.current) {
        try {
          batchEventSource.current.close();
        } catch (e) {
          /* ignore */
        }
        batchEventSource.current = null;
      }
    };
  }, [batchPollingInterval]);

  const fetchImportStatus = async (dataImportId) => {
    if (!dataImportId) return;

    try {
      // FastAPI `GET /import/status?data_import_id=<id>` — legacy POST body
      // `{data_import_name}` returned 405. Also the response shape is
      // `{message: {data: {status, success, failed, total_records}}}` now,
      // not a flat `message` object.
      const response = await importClientWrapperGet(
        urlMapping.checkImportStatus[serviceType],
        {
          data_import_id: dataImportId,
        }
      );

      const totalRecords = response?.message?.data?.total_records;
      if (totalRecords !== undefined) {
        setTotalRecordsInSheet(totalRecords);
        setTimeout(async () => {
          await validateImport(dataImportId, totalRecords);
        }, 2000);
      }
    } catch (err) {
      console.error("Error fetching import status:", err);
    }
  };

  const fetchImportLogs = async (dataImportId, totalRecords) => {
    if (!dataImportId) return;

    try {
      // FastAPI `GET /import/errors-warnings?data_import_id=<id>` returns
      // a flat list `{message: {data: [{row_index, client_name, success,
      // exception, docname}, ...]}}`. Legacy POST body `{data_import_name}`
      // returned 405; legacy response had `result.import_errors / _successes
      // / summary`. Re-wrap the new flat shape into the old Frappe-style
      // object so all the downstream rendering / polling-stop logic below
      // stays untouched.
      const raw = await importClientWrapperGet(
        urlMapping.checkImportLogs[serviceType],
        {
          data_import_id: dataImportId,
        }
      );
      const rowList = Array.isArray(raw?.message?.data) ? raw.message.data : [];
      // Renderer (Clients.jsx:1796-1834) reads Frappe-legacy fields
      // `row_indexes` (array) + `messages` (array of {message}). Backend
      // returns new fields `row_index` (single int) + `exception` (single
      // string). Map them through here so render columns populate correctly.
      const withLegacyFields = (r) => ({
        ...r,
        row_indexes: r.row_index !== undefined && r.row_index !== null ? [r.row_index] : [],
        messages: r.exception ? [{ message: r.exception }] : [],
      });
      const successes = rowList.filter((r) => r.success).map(withLegacyFields);
      const errors = rowList.filter((r) => !r.success).map(withLegacyFields);
      const response = {
        result: {
          import_errors: errors,
          import_successes: successes,
          summary: {
            success_count: successes.length,
            error_count: errors.length,
          },
        },
      };

      console.log("fetchImportLogs raw response:", response); // Debug log

      if (response && response.result) {
        const { import_errors = [], import_successes = [], summary = {} } = response.result;

        // Ensure arrays are valid
        const safeErrors = Array.isArray(import_errors) ? import_errors : [];
        const safeSuccesses = Array.isArray(import_successes) ? import_successes : [];

        // Combine errors and successes for display
        const logs = [
          ...safeSuccesses.map((s) => ({ ...s, status: "success" })),
          ...safeErrors.map((e) => ({ ...e, status: "error" })),
        ];

        console.log("Import logs combined:", logs); // Debug log

        setRealTimeImportLogs(logs);
        setLogsData(logs);

        // Check if all records are processed using summary
        const processedCount = (summary.success_count || 0) + (summary.error_count || 0);
        console.log("Processed count:", processedCount, "Total records:", totalRecords); // Debug log

        if (totalRecords > 0 && processedCount >= totalRecords) {
          // All records processed, stop polling immediately
          setImportStatus("completed");

          // Clear the polling interval using the current state
          setImportPollingInterval((currentInterval) => {
            if (currentInterval) {
              clearInterval(currentInterval);
            }
            return null;
          });

          // Refresh clients data after successful import
          setTimeout(() => {
            getClientsData(limitStart, limit);
            const docType = getClientDocType(serviceType);
            getClientsCount(docType);
          }, 1000);
        }
      }
    } catch (err) {
      console.error("Error fetching import logs:", err);
      // Don't crash the UI on error - just log it
    }
  };

  const startLogPolling = (dataImportId, totalRecords) => {
    setImportPollingInterval((currentInterval) => {
      if (currentInterval) {
        clearInterval(currentInterval);
      }
      return null;
    });

    const intervalId = setInterval(() => {
      fetchImportLogs(dataImportId, totalRecords);
    }, 5000);

    setImportPollingInterval(intervalId);
  };

  const validateImport = async (dataImportId, totalRecords) => {
    if (!dataImportId) return;

    try {
      // FastAPI `GET /import/document?data_import_id=<id>` — legacy Frappe
      // params `{doctype: "Data Import", name}` returned 422. The new backend
      // response shape is `{message: {data: {...}}}` without a
      // `template_warnings` field (which Frappe used to surface pre-start
      // column-format warnings). The check below is retained as a no-op
      // safety net — it's always false with the FastAPI response — so the
      // else-branch always runs and polling starts immediately.
      const response = await importClientWrapperGet(
        urlMapping.docValidation[serviceType],
        {
          data_import_id: dataImportId,
        }
      );

      // Check if response has template warnings
      if (response?.docs?.[0]?.template_warnings) {
        try {
          const templateWarnings = JSON.parse(
            response.docs[0].template_warnings
          );

          if (templateWarnings && templateWarnings.length > 0) {
            setImportStatus("failed");

            const formattedWarnings = templateWarnings.map(
              (warning) =>
                `Error at Row ${warning.row}, Column ${warning.col} (${
                  warning.field?.label || "Unknown field"
                }): ${warning.message.replace(/<\/?strong>/g, "")}`
            );

            setRealTimeImportLogs(formattedWarnings);
            setImportLogs(formattedWarnings);
            return;
          }
        } catch (error) {
          console.error("Error parsing template warnings:", error);
        }
      } else {
        // Start polling for logs immediately
        startLogPolling(dataImportId, totalRecords);
      }
    } catch (err) {
      console.error("Error validating import:", err);
      setImportStatus("failed");
      setImportLogs(["An error occurred while validating the import."]);
      setRealTimeImportLogs(["An error occurred while validating the import."]);
    } finally {
      setUploading(false);
    }
  };

  const startImport = async (dataImportId) => {
    if (!dataImportId) return;

    try {
      // FastAPI `POST /import/start` takes `data_import_id` as a query param,
      // not a JSON body field `data_import`. Append it to the URL; body stays
      // empty. Legacy body `{data_import}` returned 422.
      const url = `${urlMapping.startImport[serviceType]}?data_import_id=${encodeURIComponent(dataImportId)}`;
      const response = await importClientWrapper(url, null);

      fetchImportStatus(dataImportId);
    } catch (err) {
      console.error("Error fetching import status:", err);
      toast.error(err);
      setImportStatus("failed");
    } finally {
      setUploading(false);
    }
  };

  const handlePageChange = (newLimitStart, newLimit) => {
    setLimitStart(newLimitStart);
  };

  // Page-length selector + "View All" in the table footer. Changing the
  // page size resets to the first page. "View All" passes the total record
  // count so every row loads on one page.
  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setLimitStart(0);
  };

  const handleUpload = async (fileToUpload = null) => {
    // Use provided file or fall back to fileList
    const file = fileToUpload || (fileList.length > 0 ? fileList[0] : null);

    if (!file) {
      toast.error("Please select a file to upload");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("doctype", getClientDocType(serviceType));

    setUploading(true);
    setImportStatus("processing");

    try {
      const requestUrl = `${process.env.REACT_APP_API_BASE_URL}${urlMapping.importClients[serviceType]}`;
      const response = await axios.post(requestUrl, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response) {
        const importData = response?.data?.message.data;
        setDataImportId(importData?.data_import_id);
        toast.info(
          "Importing clients initiated. Please wait while we process your file."
        );

        setTimeout(async () => {
          await startImport(importData?.data_import_id);
        }, 1000);
      }
    } catch (err) {
      setImportStatus("failed");
      // FastAPI returns errors as { detail: "..." }, legacy Frappe used { message: "..." }.
      // Try both shapes; fall back to a generic message so the user is never shown undefined.
      const errMsg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Upload failed. Please try again.";
      toast.error(errMsg);
      setErrorMsg(errMsg);
      setUploading(false);
    } finally {
    }
  };

  const handleDeleteClients = async () => {
    const docType = getClientDocType(serviceType);
    try {
      await deleteClients(DOCUMENTS.DELETE, {
        doctype: docType,
        items: JSON.stringify(selectedClientIds),
      });

      toast.info("Deleted Successfully");
    } catch (err) {
      const errorMessage =
        err?.message ||
        err?.exception ||
        (typeof err === "string" ? err : "An unexpected error occurred.");

      const cleanedMessage = errorMessage.replace(/<[^>]*>?/gm, "");
      toast.error(cleanedMessage);
    } finally {
      getClientsCount(docType);
      getClientsData(limitStart, limit);
      setSelectedClientIds([]);
    }
  };

  const handleOkDeleteClient = () => {
    setInitiateDeleteModal(false);
    handleDeleteClients();
  };

  const handleCancelDelete = () => {
    setInitiateDeleteModal(false);
  };

  // Generate import status details
  const renderImportStatusDetails = () => {
    if (importStatus === "waiting") {
      return null;
    }

    return (
      <div className="mt-6 border rounded-lg p-4 bg-gray-50">
        <div className="mb-4">
          <Text strong>Import ID:</Text> <Text>{dataImportId}</Text>
        </div>

        {totalRecordsInSheet > 0 && (
          <div className="mb-4">
            <Text strong>Total Records to Process:</Text>{" "}
            <Text>{totalRecordsInSheet}</Text>
            {realTimeImportLogs.length > 0 && (
              <>
                <br />
                <Text strong>Processed:</Text>{" "}
                <Text>{realTimeImportLogs.length}</Text>
                <Text> / {totalRecordsInSheet}</Text>
              </>
            )}
          </div>
        )}

        {importStatus === "processing" && (
          <Alert
            message="Import in Progress"
            description={
              <div>
                <div>
                  Your file is being processed. Please wait while we import your
                  clients.
                </div>
                {realTimeImportLogs.length > 0 && totalRecordsInSheet > 0 && (
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${
                            (realTimeImportLogs.length / totalRecordsInSheet) *
                            100
                          }%`,
                        }}
                      ></div>
                    </div>
                    <Text className="text-sm text-gray-600 mt-1">
                      Processing records... ({realTimeImportLogs.length}/
                      {totalRecordsInSheet})
                    </Text>
                  </div>
                )}
              </div>
            }
            type="info"
            showIcon
            icon={<SyncOutlined spin />}
          />
        )}

        {importStatus === "completed" && (
          <>
            <Alert
              message="Import Process Completed"
              description="Your clients importing process have been successfully completed!"
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
            />
            <div style={{ padding: "0px 20px" }}>
              <h1></h1>
              <ImportLogsTable logData={logsData} />
              {/* <ImportPreviewTable previewData={previewData} /> */}
            </div>
          </>
        )}

        {importStatus === "failed" && (
          <>
            <Alert
              message="Import Failed"
              description="There were errors during the import process. Please check the details below."
              type="error"
              showIcon
              icon={<CloseCircleOutlined />}
            />

            {errorMsg && (
              <div className="py-1 text-red-500 mt-4">
                <Text style={{ color: "red" }}>
                  <CloseCircleFilled /> {errorMsg}
                </Text>
              </div>
            )}
          </>
        )}

        {Array.isArray(realTimeImportLogs) && realTimeImportLogs.length > 0 && (
          <div className="mt-4">
            <Text strong>Import Details:</Text>

            <div className="max-h-60 overflow-y-auto mt-2 border rounded bg-white">
              {/* Check if logs are simple strings (validation warnings) or objects */}
              {typeof realTimeImportLogs[0] === "string" ? (
                // Simple string messages (validation warnings)
                <div className="p-4">
                  {realTimeImportLogs.map((msg, idx) => (
                    <div key={idx} className="text-red-500 mb-2">
                      {msg}
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {/* Header Row */}
                  <div className="grid grid-cols-4 gap-4 bg-gray-100 py-2 px-4 font-semibold text-gray-700 border-b">
                    <div>Row Number</div>
                    <div>Client</div>
                    <div>Status</div>
                    <div>Message</div>
                  </div>

                  {/* Log Entries */}
                  {realTimeImportLogs.map((log, index) => {
                // Handle both old format (success: 0/1) and new format (status: "success"/"error")
                const isSuccess = log.success === 1 || log.success === true || log.status === "success";
                const isError = log.success === 0 || log.success === false || log.status === "error";

                // Safely parse messages
                let parsedMessages = [];
                try {
                  if (log.messages) {
                    if (typeof log.messages === "string") {
                      parsedMessages = JSON.parse(log.messages);
                    } else if (Array.isArray(log.messages)) {
                      parsedMessages = log.messages;
                    }
                  }
                } catch (e) {
                  console.error("Error parsing messages:", e);
                  parsedMessages = [];
                }

                return (
                  <div
                    key={index}
                    className={`grid grid-cols-4 gap-4 py-3 px-4 items-start border-b ${
                      isError ? "text-red-500" : "text-green-500"
                    }`}
                  >
                    {/* Row Number */}
                    <div>
                      <Text style={{ color: "#555555", fontWeight: 600 }}>
                        {log.row_indexes}
                      </Text>
                    </div>

                    {/* Client Name */}
                    <div>
                      <Text style={{ color: "#555555", fontWeight: 600 }}>
                        {log?.custom_client_name || log?.client_name || ""}
                      </Text>
                    </div>

                    {/* Status Tag */}
                    <div>
                      <Tag color={isError ? "red" : "green"}>
                        {isError ? "Failed" : "Success"}
                      </Tag>
                    </div>

                    {/* Message(s) */}
                    <div className="flex flex-col space-y-1">
                      {isSuccess ? (
                        <Text style={{ color: "green" }}>
                          Successfully Imported
                        </Text>
                      ) : (
                        parsedMessages.length > 0 ? (
                          parsedMessages.map((item, idx) => (
                            <Text key={idx} style={{ color: "red" }}>
                              {typeof item === "string" ? item : item?.message || "Error"}
                            </Text>
                          ))
                        ) : (
                          <Text style={{ color: "red" }}>Import failed</Text>
                        )
                      )}
                    </div>
                  </div>
                );
              })}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleFilterValueChange = (field, value) => {
    setFilterValues((prev) => ({
      ...prev,
      [field]: value,
    }));

    setLimitStart(0);
  };

  const buildFilters = () => {
    const filters = [];

    // Base filter for active/inactive
    filters.push(["disabled", "=", searchClientType === "active" ? 0 : 1]);

    // Client name filter
    if (filterValues.client_name) {
      filters.push(["client_name", "like", `%${filterValues.client_name}%`]);
    }

    // Username filter
    if (filterValues.username) {
      const usernameField =
        serviceType === "gstin"
          ? "gst_username"
          : serviceType === "tds"
          ? "tds_username"
          : "username";
      filters.push([usernameField, "like", `%${filterValues.username}%`]);
    }

    return filters;
  };

  return (
    <>
      <div className="flex w-full items-center justify-between">
        <Breadcrumb
          items={[
            {
              title: (
                <TenantLink
                  className="exempt-from-readonly"
                  to={{
                    pathname: "/",
                  }}
                >
                  Home
                </TenantLink>
              ),
            },
            {
              title: (
                <TenantLink
                  className="exempt-from-readonly"
                  to={{
                    pathname:
                      serviceType == "gstin"
                        ? "/gstin"
                        : serviceType == "tds"
                        ? "/tds"
                        : "/income-tax",
                  }}
                >
                  {serviceType == "gstin"
                    ? "GST"
                    : serviceType == "tds"
                    ? "TDS"
                    : "Income Tax"}
                </TenantLink>
              ),
            },
            {
              title:
                searchClientType == "active"
                  ? "Active Clients"
                  : "Disabled Clients",
            },
          ]}
          style={{
            marginBottom: "10px",
            color: "#151c40",
          }}
        />
        <DashboardButton serviceType={serviceType} />
      </div>

      <PageTitle
        title={
          searchClientType == "active" ? "Active Clients" : "Disabled Clients"
        }
        navigate={() => navigate(-1)}
      />

      {/* Action Buttons */}
      {searchClientType == "active" && (
        <div className="flex flex-wrap gap-2 mt-4 items-center pl-4 pr-4 ">
          <div className="flex flex-wrap items-center gap-2">
            {/* <div className="flex items-center">
              {selectedClientIds.length === 0 ? (
                <Tooltip title="Please select a client">
                  <div>
                    <Button
                      type="primary"
                      icon={<DeleteFilled />}
                      className="mr-2"
                      disabled
                    >
                      Delete Clients
                    </Button>
                  </div>
                </Tooltip>
              ) : (
                <Button
                  type="primary"
                  icon={<DeleteFilled />}
                  className="mr-2"
                  onClick={() => setInitiateDeleteModal(true)}
                >
                  Delete Clients
                </Button>
              )}
            </div> */}
            {/* Embed iframe (e.g. Zoho partner) syncs clients directly via
                partner API — Add / Import affordances don't apply there
                and would confuse the partner-tenant user. Hide both when
                EmbedTokenContext has claims (i.e. we're under /embed/*). */}
            {!embedClaims && (
              <>
                <Button
                  onClick={() => {
                    setOpenClientModal(true);
                  }}
                  variant="outlined"
                  icon={<FaPlus />}
                  type="primary"
                >
                  Add{" "}
                  {serviceType == "gstin"
                    ? "GST"
                    : serviceType == "tds"
                    ? "TDS"
                    : "Income Tax"}{" "}
                  Client
                </Button>
                <Button
                  type="primary"
                  icon={<FaFileImport />}
                  onClick={checkRunningImport}
                  loading={checkingRunningImport}
                  disabled={checkingRunningImport}
                >
                  Import Clients
                </Button>
              </>
            )}
            {/* The old disabled-when-empty + active-when-selected "Fetch
                Notices" pair that lived here used to handle the
                selected-clients case. It is removed: the new unified
                "Fetch Notices" button below now plays both roles
                (renders "Fetch Notices for Selected Clients (N)" when a
                selection exists, otherwise plain "Fetch Notices"). */}
            <Button
              type="primary"
              icon={<FaArrowsRotate />}
              onClick={() => {
                getClientsData(limitStart, limit);
              }}
            >
              Refresh
            </Button>
            {/* Bulk reassign (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.3).
                ALWAYS visible for admins in a multi-user org. When no
                clients are selected, clicking shows a "select clients
                first" popup. The label reflects the current selection
                count. Regular users use the row-level button instead. */}
            {!isSingleUserOrg && isAdmin && (
              <Button
                type="primary"
                icon={<FaList />}
                onClick={() => {
                  if (selectedClientIds.length === 0) {
                    Modal.info({
                      title: "No clients selected",
                      content: "Please select clients first.",
                      okText: "OK",
                    });
                    return;
                  }
                  setReassignTargetIds([...selectedClientIds]);
                  setReassignLabel(null);
                  // Bulk: leave current assignees unset (clients may have
                  // different assignees) so the popup defaults to no
                  // selection.
                  setReassignCurrentUsers([]);
                  setReassignMode("bulk");
                }}
              >
                {selectedClientIds.length > 0
                  ? `Bulk Reassign (${selectedClientIds.length})`
                  : "Bulk Reassign"}
              </Button>
            )}
            {/* Unified Download Data button — replaces the old per-table
                CSV/PDF export. Excel + PDF, clean columns (no passwords),
                with a Yes/No confirm describing all-vs-selected scope. */}
            <DownloadDataButton
              entity={
                serviceType === "gstin"
                  ? "clients-gst"
                  : serviceType === "tds"
                  ? "clients-tds"
                  : "clients-income-tax"
              }
              noun="clients"
              selectedIds={selectedClientIds}
              selectedRows={(clientsData || []).filter((c) =>
                selectedClientIds.includes(c.id || c.name)
              )}
              fetchAllRecords={fetchAllForExport}
              fileBaseName={`noticeai-${serviceType}-clients`}
              title={
                serviceType === "gstin"
                  ? "GST Clients"
                  : serviceType === "tds"
                  ? "TDS Clients"
                  : "Income Tax Clients"
              }
            />
            {/* Unified notice-fetch button — replaces both the old
                disabled-when-empty + active-when-selected "Fetch
                Notices" pair and the separate blue-glow "Fetch Notices
                for All Clients" button. Selection wins; otherwise
                targets the everyone case via the Step-2 cooldown
                modal. */}
            <Button
              type="primary"
              icon={<Download className="w-4 h-4 mt-2" />}
              loading={isProcessingAll}
              disabled={isAnyFetchInProgress}
              onClick={() => setFetchScopedBasicOpen(true)}
              style={{
                borderRadius: "8px",
                padding: "8px 16px",
                fontWeight: "600",
              }}
            >
              {isProcessingAll
                ? "Processing..."
                : selectedClientIds.length > 0
                ? `Fetch Notices for Selected Clients (${selectedClientIds.length})`
                : "Fetch Notices"}
            </Button>
            {/* User filter dropdown (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.2).
                Component self-suppresses for single-user orgs and for
                regular users (the only meaningful scope they can apply
                is the BE default — own + Shared — already in effect). */}
            <UserFilterDropdown
              value={userFilter}
              onChange={setUserFilter}
              portal={serviceType}
            />
            {/* The old blue-glow "Fetch Notices for All Clients" button
                that lived here used to handle the everyone case. It is
                removed: the unified "Fetch Notices" button above now
                covers both selected and all-on-page in one control. */}
          </div>
        </div>
      )}

      {/* Batch fetch progress banner */}
      {fetchProgress && !fetchProgress.isComplete && (
        <div
          style={{
            marginTop: "16px",
            padding: "16px 20px",
            background: "linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%)",
            borderRadius: "12px",
            border: "1px solid #91d5ff",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <SyncOutlined spin style={{ color: "#1890ff", fontSize: "16px" }} />
              <Text strong style={{ fontSize: "14px" }}>
                Fetching {fetchProgress.portalType || ""} Notices...
              </Text>
            </div>
            <Text style={{ color: "#666", fontSize: "13px" }}>
              {fetchProgress.successful + fetchProgress.failed} / {fetchProgress.total} clients processed
            </Text>
          </div>
          <Progress
            percent={fetchProgress.total > 0 ? Math.round(((fetchProgress.successful + fetchProgress.failed) / fetchProgress.total) * 100) : 0}
            strokeColor={{
              '0%': '#108ee9',
              '100%': '#87d068',
            }}
            size="small"
          />
          <div style={{ display: "flex", gap: "16px", marginTop: "6px", fontSize: "12px", color: "#666" }}>
            <span style={{ color: "#52c41a" }}>{fetchProgress.successful} successful</span>
            {fetchProgress.failed > 0 && <span style={{ color: "#ff4d4f" }}>{fetchProgress.failed} failed</span>}
          </div>
        </div>
      )}

      <div
        className="border-[2px] mt-4  rounded-2xl "
        style={{
          borderColor: "#d1d5db",
        }}
      >
        <TableV2
          columns={columns}
          data={clientsData}
          isLoading={clientLoading}
          limitStart={limitStart}
          limit={limit}
          setLimitStart={setLimitStart}
          totalRecords={totalRecords}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          type="clients"
          serviceType={serviceType}
          rowRedirection={handleRowRedirection}
          selectedIds={selectedClientIds}
          setSelectedIds={setSelectedClientIds}
          filterValues={filterValues}
          handleFilterValueChange={handleFilterValueChange}
          statusOptions={[]}
          title={
            searchClientType == "active" ? "Active Clients" : "Disabled Clients"
          }
          onFetchAllRecords={fetchAllForExport}
        />
      </div>

      <ReassignClientsModal
        open={reassignMode !== null}
        portal={portalSegment}
        clientIds={reassignTargetIds}
        clientLabel={reassignLabel}
        currentAssignedUsers={reassignCurrentUsers}
        onCancel={() => setReassignMode(null)}
        onSuccess={() => {
          setReassignMode(null);
          setSelectedClientIds([]);
          getClientsData(limitStart, limit);
        }}
      />

      <AddClientModal
        open={openClientModal}
        handleClose={() => setOpenClientModal(false)}
        handleSubmit={handleAddClient}
        type={
          serviceType == "income-tax"
            ? "income-tax"
            : serviceType == "tds"
            ? "TDS"
            : "GST"
        }
        title={
          serviceType == "income-tax"
            ? "Income Tax"
            : serviceType == "tds"
            ? "TDS"
            : "GST"
        }
      />

      <Modal
        title="Confirmation"
        open={fetchNoticeModal}
        onOk={handleOk}
        onCancel={handleCancel}
        centered
        style={{ top: -50 }}
      >
        <div className="flex items-center justify-center flex-col text-center">
          <img
            src={FetchNoticesImg}
            width={250}
            height={250}
            className="rounded-lg"
          />
          <p className="text-lg mt-4">
            Are you sure you want to fetch{" "}
            <strong>
              {serviceType == "income-tax"
                ? "Income Tax"
                : serviceType == "tds"
                ? "TDS"
                : "GST"}{" "}
            </strong>
            notices for <strong>{selectedClient?.client_name}</strong> ?
          </p>
        </div>
      </Modal>

      <ImportClientsModal
        importClientModal={importClientModal}
        closeImportClientModal={closeImportClientModal}
        serviceType={serviceType}
        handleUpload={handleUpload}
        uploading={uploading}
        importStatus={importStatus}
        downloadTemplate={downloadTemplate}
        renderImportStatusDetails={renderImportStatusDetails}
      />

      <Modal
        title=""
        open={initiateLDeleteModal}
        onOk={handleOkDeleteClient}
        onCancel={handleCancelDelete}
        centered
        zIndex={9999}
        style={{ top: -50 }}
      >
        <div className="flex items-center justify-center flex-col">
          <img src={WarningImage} width={250} height={250} />
          <p>Are you sure you want to Delete ?</p>
        </div>
      </Modal>

      <Modal
        title={
          <div className="flex items-center gap-2">
            <CheckCircleOutlined
              style={{ color: fetchProgress?.failed > 0 ? "#faad14" : "#52c41a", fontSize: "20px" }}
            />
            <span>
              {fetchProgress?.failed > 0 && fetchProgress?.successful === 0
                ? "Sync Process Failed"
                : "Sync Process Completed"}
            </span>
          </div>
        }
        open={syncCompletionModal}
        onCancel={() => {
          getClientsData(limitStart, limit);
          const docType = getClientDocType(serviceType);
          getClientsCount(docType);
          setSyncCompletionModal(false);
          setCompletedClients([]);
          setFetchProgress(null);
          setParallelFetchLogName(null);
          setIsProcessingAll(false);
        }}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => {
              getClientsData(limitStart, limit);
              const docType = getClientDocType(serviceType);
              getClientsCount(docType);

              setSyncCompletionModal(false);
              setCompletedClients([]);
              setFetchProgress(null);
              setParallelFetchLogName(null);
              setIsProcessingAll(false);
            }}
          >
            OK
          </Button>,
        ]}
        width={500}
        centered
        closable={false}
      >
        <div style={{ padding: "16px 0" }}>
          {fetchProgress ? (
            <>
              <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <Title level={4} style={{ color: fetchProgress.failed > 0 ? "#faad14" : "#52c41a", marginBottom: "8px" }}>
                  {fetchProgress.portalType || ""} Notice Fetching Complete
                </Title>
                <Text type="secondary">
                  All {fetchProgress.total} clients have been processed.
                </Text>
              </div>

              <div style={{
                display: "flex",
                justifyContent: "center",
                gap: "32px",
                marginBottom: "16px",
                padding: "16px",
                background: "#fafafa",
                borderRadius: "8px",
              }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: "bold", color: "#52c41a" }}>
                    {fetchProgress.successful}
                  </div>
                  <Text type="secondary" style={{ fontSize: "12px" }}>Successful</Text>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: "bold", color: fetchProgress.failed > 0 ? "#ff4d4f" : "#d9d9d9" }}>
                    {fetchProgress.failed}
                  </div>
                  <Text type="secondary" style={{ fontSize: "12px" }}>Failed</Text>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: "bold", color: "#1890ff" }}>
                    {fetchProgress.total}
                  </div>
                  <Text type="secondary" style={{ fontSize: "12px" }}>Total</Text>
                </div>
              </div>

              {fetchProgress.failed > 0 && (
                <div style={{ marginBottom: "12px" }}>
                  {fetchProgress.portalFailureCount > 0 && (
                    <Alert
                      message={`${fetchProgress.portalFailureCount} client(s) could not be fetched due to portal connectivity issues. You may retry later.`}
                      type="warning"
                      showIcon
                      style={{ marginBottom: "8px" }}
                    />
                  )}
                  {fetchProgress.credentialFailureCount > 0 && (
                    <Alert
                      message={`${fetchProgress.credentialFailureCount} client(s) failed due to login credentials / other issues.`}
                      type="error"
                      showIcon
                      style={{ marginBottom: "8px" }}
                    />
                  )}
                  <Text type="secondary" style={{ fontSize: "13px", display: "block", textAlign: "center", marginTop: "4px" }}>
                    Check the Failed Login tab for details.
                  </Text>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: "center", marginBottom: "16px" }}>
              <Title level={4} style={{ color: "#52c41a", marginBottom: "8px" }}>
                Notice Syncing Completed!
              </Title>
              {completedClients.length > 0 && (
                <Text type="secondary">
                  {completedClients.length} client(s) synced successfully.
                </Text>
              )}
            </div>
          )}
        </div>
      </Modal>

      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <CheckCircle color="#52c41a" size={20} />
            <span>Successfully Queued</span>
          </div>
        }
        centered
        open={showSuccessModal}
        onOk={() => {
          setShowSuccessModal(false);
          getClientsData(limitStart, limit);
          if (parallelFetchLogName) {
            startBatchPolling(parallelFetchLogName);
          } else {
            // TDS or non-Lambda path: re-enable button since there's no batch-level tracking
            setIsProcessingAll(false);
          }
        }}
        onCancel={() => {
          setShowSuccessModal(false);
          getClientsData(limitStart, limit);
          if (parallelFetchLogName) {
            startBatchPolling(parallelFetchLogName);
          } else {
            setIsProcessingAll(false);
          }
        }}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => {
              setShowSuccessModal(false);
              getClientsData(limitStart, limit);
              if (parallelFetchLogName) {
                startBatchPolling(parallelFetchLogName);
              } else {
                setIsProcessingAll(false);
              }
            }}
          >
            Got It
          </Button>,
        ]}
        width={500}
      >
        <div style={{ padding: "16px 0" }}>
          <p style={{ marginBottom: "16px", fontSize: "16px" }}>
            <strong>
              Notice fetching has been successfully queued for all clients.
            </strong>
          </p>

          <p style={{ marginBottom: "16px", color: "#666" }}>
            Notices are being fetched in the background. You will see a live
            progress bar and a notification when the process completes.
          </p>

          {processedClients.length > 0 && (
            <div>
              <p style={{ marginBottom: "8px", fontWeight: "bold" }}>
                Queued for {processedClients.length} clients:
              </p>
              <div
                style={{
                  maxHeight: "150px",
                  overflowY: "auto",
                  background: "#f5f5f5",
                  padding: "8px",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              >
                {processedClients.map((client, index) => (
                  <div key={index} style={{ padding: "2px 0" }}>
                    • {client}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        title={
          <div className="flex items-center gap-2">
            <SyncOutlined spin style={{ color: "#1890ff", fontSize: "20px" }} />
            <span>Import Already in Progress</span>
          </div>
        }
        open={runningImportModal}
        onCancel={() => {
          setRunningImportModal(false);
          setRunningImportData(null);
        }}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => {
              setRunningImportModal(false);
              setRunningImportData(null);
            }}
          >
            OK, I'll Wait
          </Button>,
        ]}
        width={600}
        centered
      >
        <div className="py-4">
          <div className="text-center mb-4">
            <div className="mb-0">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                <FileTextOutlined
                  style={{ fontSize: "32px", color: "#1890ff" }}
                />
              </div>
            </div>
            <Title level={4} style={{ color: "#1890ff", marginBottom: "5px" }}>
              Client Import Currently Running
            </Title>
            <Text type="secondary" style={{ fontSize: "15px" }}>
              Another client import process is currently running. Please wait
              for it to complete before starting a new import.
            </Text>
          </div>

          {runningImportData?.imports &&
            runningImportData.imports.length > 0 && (
              <div className="mb-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 overflow-hidden h-50">
                  <Text
                    strong
                    className="block mb-3"
                    style={{ color: "#1890ff" }}
                  >
                    Running Import Details:
                  </Text>
                  {runningImportData.imports
                    .slice(0, 1)
                    .map((importItem, index) => (
                      <div
                        key={index}
                        className="bg-white rounded-md p-3 mb-2 border border-blue-100"
                        style={{ fontSize: "14px" }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-gray-700">
                            Import Name:
                          </span>
                          <Tag color="blue" icon={<SyncOutlined spin />}>
                            {importItem.status || "Running"}
                          </Tag>
                        </div>
                        <div className="space-y-1 text-gray-600">
                          <div className="flex justify-between">
                            <span>Data Import:</span>
                            <span className="font-medium text-gray-800">
                              {importItem.data_import}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Started By:</span>
                            <span className="font-medium text-gray-800">
                              {importItem.user}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Started At:</span>
                            <span className="font-medium text-gray-800">
                              {moment(importItem.started_at).format(
                                "MMMM Do YYYY"
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <Text className="text-yellow-800">
                  <strong>Please Note:</strong> You can refresh this page or try
                  again in a few minutes. The running import must complete
                  before you can start a new one.
                </Text>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title={
          <div className="flex items-center gap-2">
            <ExclamationCircleOutlined className="text-red-500" />
            <span>Import Error Logs</span>
          </div>
        }
        open={errorLogsModal}
        onCancel={() => setErrorLogsModal(false)}
        width={1000}
        footer={[
          <Button
            key="download"
            icon={<DownloadOutlined />}
            onClick={downloadErrorTemplate}
            type="primary"
            ghost
          >
            Download Error Template
          </Button>,
          <Button key="close" onClick={() => setErrorLogsModal(false)}>
            Close
          </Button>,
        ]}
        centered
      >
        <div className="py-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileExcelOutlined className="text-lg" />
              <span className="font-medium">
                Total Errors: <Tag color="red">{errorLogsData?.length}</Tag>
              </span>
            </div>
            <span className="text-sm text-gray-500">
              Import Name:{" "}
              <span className="font-mono text-xs">{errorImportName}</span>
            </span>
          </div>
          <ErrorLogsTable
            logsData={errorLogsData}
            summaryData={summaryData}
            loading={false}
          />

          {/* <Table
            columns={errorColumns}
            dataSource={errorLogsData?.filter((row) => row.success === 0)}
            rowKey={(record) => `${record.row}-${record.field}`}
            loading={loadingErrorLogs}
            pagination={{
              pageSize: 8,
              showSizeChanger: false,
              showTotal: (total) => `Total ${total} errors`,
            }}
            scroll={{ x: 800 }}
            size="small"
          /> */}
        </div>
      </Modal>

      {/* Step 1 of 2 — basic Yes/No confirm with selection count.
          Yes → open the rich cooldown table (Step 2). */}
      <Modal
        open={fetchScopedBasicOpen}
        title="Fetch notices"
        centered
        onCancel={() => setFetchScopedBasicOpen(false)}
        onOk={() => {
          setFetchScopedBasicOpen(false);
          setFetchScopedConfirmOpen(true);
        }}
        okText="Yes"
        cancelText="No"
      >
        <p className="text-gray-700">
          {selectedClientIds.length > 0
            ? `Fetch notices for ${selectedClientIds.length} selected client(s)?`
            : `Fetch notices for all clients?`}
        </p>
      </Modal>
      {/* Step 2 of 2 — rich cooldown table. Scoped to the user's
          pre-selection when one exists, otherwise covers the
          everyone case. */}
      <FetchNoticesConfirmModal
        open={fetchScopedConfirmOpen}
        onCancel={() => setFetchScopedConfirmOpen(false)}
        onConfirm={(ids) => {
          setFetchScopedConfirmOpen(false);
          handleFetchAllClientsNotices(ids);
        }}
        portal={serviceType}
        clientIds={
          selectedClientIds.length > 0 ? selectedClientIds : undefined
        }
      />
      <UniversalLoader show={syncNoticeLoading} text={"Fetching Notices"} />
    </>
  );
};
